import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface MuskingumCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  failedRecords: Array<{ line: number; error: string }>;
}

export const MuskingumCountyTextParser = ({ onRecordsSaved }: MuskingumCountyTextParserProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pastedText, setPastedText] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const parseDate = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    // MM/DD/YYYY format
    if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [month, day, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Already in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    
    return null;
  };

  const parseMuskingumCountyText = (text: string): any[] => {
    console.log('=== STARTING MUSKINGUM PARSE ===');
    console.log('Input text length:', text.length);
    
    const lines = text.split('\n');
    console.log('Total lines:', lines.length);
    const records = [];
    
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      
      // Look for a line that contains OFF followed by a date and LAND CONTRACT
      if (line.includes('OFF') && line.includes('LAND CONTRACT')) {
        console.log(`\n=== Found potential record at line ${i} ===`);
        console.log('Line:', line);
        
        const parts = line.split('\t').filter(p => p.trim());
        console.log('Parts:', parts);
        
        let dateStr = '';
        let offIndex = -1;
        
        // Find OFF and the date
        for (let j = 0; j < parts.length; j++) {
          if (parts[j] === 'OFF' && j + 1 < parts.length) {
            offIndex = j;
            dateStr = parts[j + 1];
            break;
          }
        }
        
        console.log('Date found:', dateStr);
        
        // Collect all subsequent lines until we hit the document number pattern
        let namesAndProperty: string[] = [];
        let documentNumber = '';
        let j = i + 1;
        
        while (j < lines.length) {
          const nextLine = lines[j].trim();
          console.log(`Line ${j}:`, nextLine);
          
          // Check if this is the document number (format: #### / ###)
          if (/^\d{4}\s*\/\s*\d{1,4}$/.test(nextLine)) {
            documentNumber = nextLine.replace(/\s/g, '');
            console.log('Found doc number:', documentNumber);
            break;
          }
          
          // Check if we've hit the next record (starts with number then OFF)
          if (nextLine.includes('OFF') && nextLine.includes('LAND CONTRACT')) {
            console.log('Hit next record, stopping');
            break;
          }
          
          // Skip L/C, pages, flag lines
          if (nextLine === 'L/C' || nextLine === 'CONTRACT FOR DEED' || 
              nextLine.includes('pages') || nextLine.includes('Flag') ||
              nextLine === '' || nextLine.startsWith('-----')) {
            j++;
            continue;
          }
          
          namesAndProperty.push(nextLine);
          j++;
        }
        
        console.log('Names and property collected:', namesAndProperty);
        
        if (documentNumber && namesAndProperty.length > 0) {
          // Split names from property - first few lines are usually names
          // Property lines typically contain keywords like TOWNSHIP, LOT, ADD, etc.
          const propertyKeywords = ['TOWNSHIP', 'Section:', 'LOT', 'PARCEL', 'ZANESVILLE', 
                                    'ROSEVILLE', 'PHILO', 'DRESDEN', 'FRAZEYSBURG', 'ADD', 
                                    'SUB', 'ACRES', 'A SEE', 'PUTNAM', 'BRIGHTON', 'FULTONHAM', 
                                    'NEWTON', 'FALLS', 'CITY', 'QT', 'PT'];
          
          let splitIndex = 0;
          for (let k = 0; k < namesAndProperty.length; k++) {
            const hasPropertyKeyword = propertyKeywords.some(kw => 
              namesAndProperty[k].toUpperCase().includes(kw)
            );
            if (hasPropertyKeyword) {
              splitIndex = k;
              break;
            }
          }
          
          const nameLines = namesAndProperty.slice(0, splitIndex || Math.ceil(namesAndProperty.length / 2));
          const propertyLines = namesAndProperty.slice(splitIndex || Math.ceil(namesAndProperty.length / 2));
          
          console.log('Name lines:', nameLines);
          console.log('Property lines:', propertyLines);
          
          const grantorName = nameLines[0] || '';
          const granteeName = nameLines.slice(1).join(' ');
          const propertyAddress = propertyLines.join(' ');
          
          // Parse date
          const filingDate = parseDate(dateStr) || new Date().toISOString().split('T')[0];
          
          console.log('Creating record:', {
            document_number: documentNumber,
            filing_date: filingDate,
            grantor_name: grantorName,
            grantee_name: granteeName,
            property_address: propertyAddress
          });
          
          records.push({
            county: 'Muskingum',
            document_number: documentNumber,
            filing_date: filingDate,
            document_type: 'LAND CONTRACT',
            grantor_name: grantorName,
            grantee_name: granteeName,
            property_address: propertyAddress,
            search_date: new Date().toISOString().split('T')[0],
            is_potential_owner_finance: true,
            confidence_score: 0.8,
            detection_reasons: ['Document type: LAND CONTRACT', 'Muskingum County format'],
          });
        }
        
        i = j;
      } else {
        i++;
      }
    }
    
    console.log(`\n=== PARSE COMPLETE: Found ${records.length} records ===`);
    return records;
  };

  const handleImport = async () => {
    if (!pastedText.trim()) {
      toast.error("Please paste some text first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const records = parseMuskingumCountyText(pastedText);
      
      if (records.length === 0) {
        toast.error("No valid records found in the pasted text");
        setIsProcessing(false);
        return;
      }

      console.log('Parsed records:', records);

      let successCount = 0;
      let failedCount = 0;
      const failedRecords: Array<{ line: number; error: string }> = [];

      for (let i = 0; i < records.length; i++) {
        try {
          const { error } = await supabase
            .from('county_research')
            .insert(records[i]);

          if (error) throw error;
          successCount++;
        } catch (error: any) {
          console.error(`Failed to insert record ${i}:`, error);
          failedCount++;
          failedRecords.push({
            line: i + 1,
            error: error.message || 'Unknown error'
          });
        }

        setProgress(Math.round(((i + 1) / records.length) * 100));
      }

      setImportResult({
        success: successCount,
        failed: failedCount,
        failedRecords
      });

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} record(s)`);
        onRecordsSaved?.();
        setPastedText('');
      }

      if (failedCount > 0) {
        toast.error(`Failed to import ${failedCount} record(s)`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to process records');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Muskingum County Text Parser</CardTitle>
        <CardDescription>
          Paste the Muskingum County land contract data directly from your spreadsheet or document
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Paste Muskingum County Data</label>
          <Textarea
            placeholder="Paste the full data here (including line numbers, dates, document types, names, and property descriptions)..."
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            className="min-h-[300px] font-mono text-sm"
          />
        </div>

        <Button 
          onClick={handleImport}
          disabled={isProcessing || !pastedText.trim()}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Upload className="mr-2 h-4 w-4 animate-pulse" />
              Processing... {progress}%
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Import Muskingum County Records
            </>
          )}
        </Button>

        {isProcessing && (
          <Progress value={progress} className="w-full" />
        )}

        {importResult && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Import Results:</p>
                <p className="text-green-600">✓ Successfully imported: {importResult.success} records</p>
                {importResult.failed > 0 && (
                  <>
                    <p className="text-red-600">✗ Failed: {importResult.failed} records</p>
                    {importResult.failedRecords.length > 0 && (
                      <details className="text-sm">
                        <summary className="cursor-pointer">View failed records</summary>
                        <ul className="mt-2 space-y-1">
                          {importResult.failedRecords.map((fail, idx) => (
                            <li key={idx}>
                              Line {fail.line}: {fail.error}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
